import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import { FirebaseService } from 'src/app/services/firebase.service';
import {OrderService} from 'src/app/services/order.service';
import {UserService} from 'src/app/services/user.service';
import {
  FEToBEProduct,
  getProductPrice,
  getProduct,
  getCalculatorItems,
  FEToBEWrappingName,
  BEToFEWrappingName,
  getCover,
  getBookDataSpecs,
} from 'src/app/functions';
import {CouponData, Product, Wrapping, Cover} from 'src/app/interfaces';
// import {product} from 'src/app/static';
import {CalculatorComponentItem} from 'src/app/shared/calculator/calculator.component';
import { LIMIT_BOOKS_TO_ORDER } from '@functions/constants';
import {Logger} from 'src/app/services/logger.service';
import {StripeService} from 'src/app/services/stripe.service';
import {MatDialog} from '@angular/material/dialog';
import {DeleteProductDialogComponent} from '../delete-product-dialog/delete-product-dialog.component';
import { RoutingService } from 'src/app/services/routing.service';
import { IShipping } from 'src/app/data/shipping/types';
import { getCurrentShipping } from 'src/app/data/shipping/utils';
import Subscriber from 'src/app/subscriber';
import { wrappings } from 'src/app/static';
import { LoaderService } from 'src/app/services/loader.service';
import { DiscountService } from 'src/app/services/discount.service';
import { getBooksDiscount } from 'src/app/services/discount.service/discount.functions';
import { take } from 'rxjs/operators';
import { IDiscountCampaign } from 'src/app/services/discount.service/discount.interfaces';
import { OrderData, BookInOrder } from '@functions/interfaces';

const sub = new Subscriber();
const log = new Logger('SectionCartComponent');

@Component({
  selector: 'app-section-cart',
  templateUrl: './section-cart.component.html',
  styleUrls: ['./section-cart.component.scss'],
})
export class SectionCartComponent implements OnInit, OnDestroy {
  public orderId: OrderData['orderId'];

  public products: Product[] = [];

  public calculatorItems: CalculatorComponentItem[];

  private _isProceed: boolean;

  public readonly getBooksDiscount = getBooksDiscount;

  get isLimitExceeded() {
    return this.products.length > LIMIT_BOOKS_TO_ORDER;
  }

  get isProceed(): boolean {
    return !!(
      !this._isProceed &&
      this.userService.booksList.length &&
      this.products.length &&
      !this.isLimitExceeded
    );
  }

  set isProceed(status: boolean) {
    this._isProceed = status;
  }

  get shipping(): IShipping {
    return getCurrentShipping(this.products.length);
  }

  get booksInOrder(): BookInOrder[] {
    return this.products.map(FEToBEProduct);
  }

  constructor(
    public router: Router,
    private orderService: OrderService,
    private _firebaseService: FirebaseService,
    public userService: UserService,
    private stripeService: StripeService,
    private dialog: MatDialog,
    private _routing: RoutingService,
    private _loaderService: LoaderService,
    public discountService: DiscountService,
  ) {
    this.createOrder();
    this.updateCalculatorItems(this.products);
  }

  public readonly getProductPrice = getProductPrice;

  ngOnInit() {
    sub.push(
      this.userService.booksInCartListObservable.subscribe(books => {
        log.info('load: _userServiceBooksList map');

        const products = books.map(book => getProduct({
          alias: book.alias,
          bookId: book.bookId,
          hero: book.heroName,
          gender: 'gender' in book ? book.gender : undefined,
          children: 'grandchildren' in book
            ? book.grandchildren.data.map(({ childrenName }) => childrenName)
            : undefined,
          wrapping: book.wrapping && wrappings.find(w => w.name === BEToFEWrappingName(book.wrapping)),
          cover: getCover(book.alias, book.cover),
          specs: getBookDataSpecs(book, ['表紙の種類']),
        }));

        this.products = products;

        this.updateCalculatorItems(products);
      }),

      // handle couponUpdate to update calculation
      this.stripeService.couponUpdate.subscribe((data: {
        [orderId: string]: CouponData
      }) => {
        const couponData = data[this.orderId];

        // check if coupon with our order id was updated
        if (couponData) {
          this.updateCalculatorItems(undefined, undefined, couponData);
        }
      }),

      this.discountService.activeDiscountCampaignsObservable.subscribe(discountCampaign => {
        this.updateCalculatorItems(undefined, discountCampaign);
      }),
    );
  }

  ngOnDestroy() {
    sub.unsubscribe();
  }

  public removeProduct(bookId: Product['bookId']) {
    const dialogRef = this.dialog.open(DeleteProductDialogComponent);

    dialogRef.afterClosed().subscribe(async (result) => {
      log.info(`Dialog result: ${result}`);

      if (result) {
        await this._firebaseService.deleteBook(bookId);
      }
    });
  }

  public async updateCalculatorItems(
    products = this.products,
    discountCampaign?: IDiscountCampaign,
    couponData = this.stripeService.coupons[this.orderId],
  ) {
    discountCampaign = discountCampaign || await this.discountService
      .activeDiscountCampaignsObservable
      .pipe(take(1)).toPromise();

    this.calculatorItems = getCalculatorItems(
      products,
      discountCampaign,
      couponData,
      this.shipping,
    );
  }

  public async selectWrapping(p: Product, name: Wrapping['name']) {
    const wrapping = p.wrappings.find(w => w.name === name);

    if (!wrapping) {
      log.error('selectWrapping - wrapping not found by name', name, 'in product', p);
      return;
    }

    await this._firebaseService.updateBookWrapping(p.bookId, FEToBEWrappingName(name));
  }

  public async selectCover(p: Product, name: Cover['name']) {
    const cover = p.covers.find(c => c.name === name);

    if (!cover) {
      log.error('selectCover - cover not found by name', name, 'in product', p);
      return;
    }

    await this._firebaseService.updateBookCover(p.bookId, name);
  }

  public addAnotherBookButtonClick(): void {
    // log.info('addAnotherBookButtonClick')
    this._routing.goToAllBooks();
  }

  /**
   * Navigates to order page. Make sure you do check by isProceed
   * before triggering this funciton.
   */
  async proceed() {
    this.isProceed = true;

    this._loaderService.show();

    if (this.orderId) {
      await this.updateOrder();
    } else {
      await this.createOrder();
    }

    this.isProceed = false;
    this.router.navigate(['order', this.orderId]);
  }

  /**
   * Returns orderId and creates new order if we didn`t
   * have it before
   */
  private async createOrder(): Promise<OrderData['orderId']> {
    if (!this.orderId) {
      this.orderId = await this.orderService.createOrder(this.booksInOrder);
    }

    return this.orderId;
  }

  private async updateOrder() {
    const order = this.orderService.store[this.orderId];

    if (!order) {
      log.error('order not found by id', this.orderId);
      return;
    }

    order.books = this.booksInOrder;

    await this.orderService.updateOrder(order);
  }
}
